import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/content';

const categoryImages: Record<string, string> = {
  Shoes: 'https://readdy.ai/api/search-image?query=Single%20pair%20of%20modern%20sneakers%20on%20warm%20wooden%20surface%20soft%20natural%20lighting%20product%20photography%20clean%20composition%20warm%20beige%20tones&width=80&height=80&seq=vigor-v2-drop-shoes&orientation=squarish',
  'Hoodies/Sweaters': 'https://readdy.ai/api/search-image?query=Premium%20quality%20hoodie%20sweater%20folded%20neatly%20on%20cream%20surface%20soft%20diffused%20lighting%20minimalist%20product%20photography%20warm%20neutral%20tones&width=80&height=80&seq=vigor-v2-drop-hoodies&orientation=squarish',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=Classic%20cotton%20tshirt%20neatly%20folded%20on%20light%20wooden%20surface%20clean%20studio%20lighting%20product%20photography%20warm%20tones&width=80&height=80&seq=vigor-v2-drop-tshirts&orientation=squarish',
  Jackets: 'https://readdy.ai/api/search-image?query=Stylish%20fashion%20jacket%20on%20wooden%20hanger%20against%20warm%20neutral%20background%20soft%20natural%20lighting%20editorial%20product%20photography%20elegant&width=80&height=80&seq=vigor-v2-drop-jackets&orientation=squarish',
  'Pants/Shorts': 'https://readdy.ai/api/search-image?query=Trendy%20denim%20pants%20folded%20on%20warm%20beige%20surface%20clean%20studio%20lighting%20minimalist%20product%20photography%20casual%20style&width=80&height=80&seq=vigor-v2-drop-pants&orientation=squarish',
  Headwear: 'https://readdy.ai/api/search-image?query=Stylish%20baseball%20cap%20on%20warm%20wooden%20surface%20soft%20natural%20lighting%20clean%20product%20photography%20minimalist%20aesthetic&width=80&height=80&seq=vigor-v2-drop-headwear&orientation=squarish',
  Sets: 'https://readdy.ai/api/search-image?query=Matching%20fashion%20outfit%20set%20displayed%20on%20warm%20cream%20background%20soft%20lighting%20editorial%20product%20photography%20coordinated%20ensemble&width=80&height=80&seq=vigor-v2-drop-sets&orientation=squarish',
  'Underwear/Underpants': 'https://readdy.ai/api/search-image?query=Soft%20cotton%20underwear%20basics%20neatly%20arranged%20on%20warm%20surface%20clean%20product%20photography%20minimalist%20style%20neutral%20tones&width=80&height=80&seq=vigor-v2-drop-underwear&orientation=squarish',
  Jersey: 'https://readdy.ai/api/search-image?query=Professional%20sports%20jersey%20hanging%20on%20warm%20neutral%20background%20soft%20lighting%20clean%20product%20photography%20athletic%20aesthetic&width=80&height=80&seq=vigor-v2-drop-jersey&orientation=squarish',
  Accessories: 'https://readdy.ai/api/search-image?query=Fashion%20accessories%20watch%20and%20sunglasses%20on%20warm%20wooden%20desk%20soft%20natural%20lighting%20editorial%20product%20photography%20elegant%20composition&width=80&height=80&seq=vigor-v2-drop-accessories&orientation=squarish',
  Bags: 'https://readdy.ai/api/search-image?query=Premium%20leather%20bag%20displayed%20on%20warm%20cream%20surface%20soft%20studio%20lighting%20clean%20product%20photography%20minimalist%20aesthetic&width=80&height=80&seq=vigor-v2-drop-bags&orientation=squarish',
  Belts: 'https://readdy.ai/api/search-image?query=Quality%20leather%20belt%20rolled%20on%20warm%20wooden%20surface%20soft%20natural%20lighting%20product%20photography%20clean%20composition&width=80&height=80&seq=vigor-v2-drop-belts&orientation=squarish',
  Perfumes: 'https://readdy.ai/api/search-image?query=Elegant%20perfume%20bottle%20on%20warm%20neutral%20background%20soft%20diffused%20lighting%20product%20photography%20sophisticated%20aesthetic&width=80&height=80&seq=vigor-v2-drop-perfumes&orientation=squarish',
  'Other Stuff': 'https://readdy.ai/api/search-image?query=Miscellaneous%20fashion%20accessories%20and%20items%20arranged%20on%20warm%20cream%20surface%20soft%20lighting%20editorial%20product%20photography%20clean%20composition&width=80&height=80&seq=vigor-v2-drop-other&orientation=squarish',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  const linkClass = scrolled
    ? 'text-foreground-700 hover:text-primary-500'
    : 'text-background-50/90 hover:text-background-50';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${scrolled ? 'bg-primary-500' : 'bg-background-50/20'}`}>
              <span className={`font-bold text-sm transition-colors ${scrolled ? 'text-background-50' : 'text-background-50'}`}>V</span>
            </div>
            <span className={`font-heading text-lg font-semibold tracking-wide transition-colors ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>VIGORBUY</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}>
              Home
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}
            >
              SPREADSHEET
            </a>

            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${linkClass}`}>
                CATEGORIES
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[280px] bg-white rounded-xl border border-background-200/70 py-2 shadow-lg">
                  <div className="max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                      >
                        <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                          {categoryImages[cat.name] && (
                            <img
                              src={categoryImages[cat.name]}
                              alt={cat.name}
                              className="w-full h-full object-cover object-top"
                            />
                          )}
                        </div>
                        <span className="text-sm font-medium">{cat.name}</span>
                        {cat.hot && (
                          <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 uppercase">Hot</span>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/what-is-vigorbuy-sheet"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}
            >
              QC GUIDES
            </Link>

            <Link
              to="/guide/best-vigorbuy-spreadsheet-2026"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}
            >
              SHOPPING GUIDE
            </Link>

            <Link
              to="/what-is-vigorbuy-sheet"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}
            >
              BLOG
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'border border-foreground-300 text-foreground-700 hover:bg-background-100 hover:border-foreground-400'
                  : 'border border-background-300/60 text-background-50 hover:bg-background-50/10'
              }`}
            >
              Vigorbuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-[72px] left-0 right-0 bottom-0 bg-white overflow-y-auto">
            <div className="px-6 py-6 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-foreground-950 font-medium text-base rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-home-line text-lg w-6 h-6 flex items-center justify-center"></i>
                Home
              </Link>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-3 text-foreground-700 font-medium text-base rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-grid-line text-lg w-6 h-6 flex items-center justify-center"></i>
                SPREADSHEET
              </a>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-3 py-3 text-foreground-700 font-medium text-base rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-store-2-line text-lg w-6 h-6 flex items-center justify-center"></i>
                  CATEGORIES
                </span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-4 border-l-2 border-background-200 pl-4 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-background-100 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                        {categoryImages[cat.name] && (
                          <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover object-top" />
                        )}
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}

              <Link
                to="/what-is-vigorbuy-sheet"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-foreground-700 font-medium text-base rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-search-eye-line text-lg w-6 h-6 flex items-center justify-center"></i>
                QC GUIDES
              </Link>

              <Link
                to="/guide/best-vigorbuy-spreadsheet-2026"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-foreground-700 font-medium text-base rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-book-open-line text-lg w-6 h-6 flex items-center justify-center"></i>
                SHOPPING GUIDE
              </Link>

              <Link
                to="/what-is-vigorbuy-sheet"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-foreground-700 font-medium text-base rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-article-line text-lg w-6 h-6 flex items-center justify-center"></i>
                BLOG
              </Link>
            </div>

            <div className="px-6 py-6 border-t border-background-200 mt-4 space-y-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 border border-foreground-200 text-foreground-700 rounded-lg text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer"
              >
                Vigorbuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}